import { Injectable } from '@angular/core';
import { Role, User } from '@core/model/auth/user.model';
import { FlamingoHttpService } from '@flamingo/service/flamingo-http.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { LoggedUser, Permissions } from '../model/auth/logged-user.model';

@Injectable({
  providedIn: 'root',
})
export class LoggedUserService {
  private _user$: BehaviorSubject<LoggedUser | null> = new BehaviorSubject<LoggedUser | null>(null);
  public user$: Observable<LoggedUser> = this._user$.asObservable()
    .pipe(
      filter((user: LoggedUser | null): user is LoggedUser => !!user),
    );

  constructor(
    private flamingoHttpService: FlamingoHttpService,
  ) {
  }

  get user(): LoggedUser | null {
    return this._user$.value;
  }

  public fetch(): Observable<LoggedUser> {
    return this.flamingoHttpService.get<User>(`/me`)
      .pipe(
        map(user => ({ ...user, role: { name: user.role, permissions: [] } } as LoggedUser)),
        // Set base permission
        map(user => {
          switch (user.role.name) {
            case Role.ADMIN:
              user.role.permissions.push(
                { name: Permissions.canManageAlarm },
                { name: Permissions.canManageUsers },
                { name: Permissions.canManageCapex },
                { name: Permissions.canManageOpex },
                { name: Permissions.canManageConfig },
                { name: Permissions.canManageDocument },
                { name: Permissions.canManageSelf },
              );
              break;
            case Role.USER:
            case Role.GUEST:
              user.role.permissions.push(
                { name: Permissions.canManageAlarm },
                { name: Permissions.canManageCapex },
                { name: Permissions.canManageOpex },
                { name: Permissions.canManageConfig },
                { name: Permissions.canManageDocument },
                { name: Permissions.canManageSelf },
              );
              break;
            default:
              user.role.permissions.push(
                { name: Permissions.canManageSelf },
              );
              break;
          }
          return user;
        }),
        tap((user) => this._user$.next(user)),
      );
  }

  public clear(): void {
    this._user$.next(null);
  }

  can(permission: Permissions | Permissions[]): boolean {
    if (!Array.isArray(permission)) {
      permission = [permission];
    }
    return permission.every(p => this.user?.role?.permissions.map(v => v.name).includes(p));
  }

  canAtLeastOne(permission: Permissions | Permissions[]): boolean {
    if (!Array.isArray(permission)) {
      permission = [permission];
    }
    return permission.some(p => this.user?.role?.permissions.map(v => v.name).includes(p));
  }

  cannot(permission: Permissions | Permissions[]): boolean {
    if (!Array.isArray(permission)) {
      permission = [permission];
    }
    return !permission.every(p => this.user?.role?.permissions.map(v => v.name).includes(p));
  }

}
